import { useState, useRef, useCallback, useEffect } from 'react';

type TTSStatus = 'idle' | 'playing' | 'paused' | 'error';

interface UseTTSReturn {
  status: TTSStatus;
  rate: number;
  errorMessage: string;
  play: (text: string) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setRate: (r: number) => void;
}

/** テキストを約1000文字ごとに句点・改行で分割 */
function splitChunks(text: string, maxLen = 1000): string[] {
  if (text.length <= maxLen) return [text];

  const chunks: string[] = [];
  let remaining = text;

  while (remaining.length > 0) {
    if (remaining.length <= maxLen) {
      chunks.push(remaining);
      break;
    }

    let splitPos = -1;
    // maxLen 以内で最後の句点・改行を探す
    for (let i = maxLen; i >= maxLen * 0.5; i--) {
      const ch = remaining[i];
      if (ch === '。' || ch === '.' || ch === '\n') {
        splitPos = i + 1;
        break;
      }
    }
    if (splitPos === -1) splitPos = maxLen;

    chunks.push(remaining.slice(0, splitPos));
    remaining = remaining.slice(splitPos);
  }

  return chunks;
}

export function useTTS(): UseTTSReturn {
  const [status, setStatus] = useState<TTSStatus>('idle');
  const [rate, setRateState] = useState(1.0);
  const [errorMessage, setErrorMessage] = useState('');

  const chunksRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const rateRef = useRef(1.0);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isCancelledRef = useRef(false);

  // rate を ref にも同期 & 再生中なら現在位置から再開
  const setRate = useCallback((r: number) => {
    console.log(`[TTS] setRate called: ${rateRef.current}x -> ${r}x`);
    setRateState(r);
    rateRef.current = r;

    // 再生中なら現在のチャンクから再開
    if (status === 'playing' && chunksRef.current.length > 0) {
      console.log(`[TTS] Restarting from chunk ${indexRef.current} with new rate ${r}x`);
      const currentIndex = indexRef.current;
      
      // 現在の再生を停止
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      
      // 同じチャンクから再開
      isCancelledRef.current = false;
      speakChunk(currentIndex);
    }
  }, [status]);

  const cancelSynth = useCallback(() => {
    console.log('[TTS] cancelSynth called');
    isCancelledRef.current = true;
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      console.log('[TTS] speechSynthesis.cancel() executed');
    }
    utteranceRef.current = null;
  }, []);

  const speakChunk = useCallback((index: number) => {
    console.log(`[TTS] speakChunk called: index=${index}, total=${chunksRef.current.length}`);
    
    if (index >= chunksRef.current.length) {
      console.log('[TTS] All chunks completed');
      setStatus('idle');
      return;
    }

    const synth = window.speechSynthesis;
    if (!synth) {
      console.error('[TTS] speechSynthesis not available');
      setStatus('error');
      setErrorMessage('このブラウザでは読み上げが不安定です');
      return;
    }

    const chunkText = chunksRef.current[index];
    console.log(`[TTS] Creating utterance for chunk ${index}, length=${chunkText.length}`);
    console.log(`[TTS] Chunk preview: "${chunkText.slice(0, 100)}..."`);

    const utt = new SpeechSynthesisUtterance(chunkText);
    utt.lang = 'ja-JP';
    utt.rate = rateRef.current;

    utt.onstart = () => {
      console.log(`[TTS] utterance onstart: chunk ${index}`);
      console.log(`[TTS] speechSynthesis state: speaking=${synth.speaking}, paused=${synth.paused}, pending=${synth.pending}`);
    };

    utt.onend = () => {
      console.log(`[TTS] utterance onend: chunk ${index}`);
      if (isCancelledRef.current) {
        console.log('[TTS] Cancelled, not proceeding to next chunk');
        return;
      }
      indexRef.current = index + 1;
      speakChunk(index + 1);
    };

    utt.onerror = (e) => {
      console.error(`[TTS] utterance onerror: chunk ${index}`, e);
      console.error(`[TTS] error type: ${e.error}, message: ${e.message}`);
      // interrupted は手動停止なので無視
      if (e.error === 'interrupted' || e.error === 'canceled') {
        console.log('[TTS] Error is interrupted/canceled, ignoring');
        return;
      }
      setStatus('error');
      setErrorMessage('このブラウザでは読み上げが不安定です');
    };

    utteranceRef.current = utt;
    
    console.log(`[TTS] Calling speechSynthesis.speak() for chunk ${index}`);
    synth.speak(utt);
    
    // speak直後の状態確認
    setTimeout(() => {
      console.log(`[TTS] After speak: speaking=${synth.speaking}, paused=${synth.paused}, pending=${synth.pending}`);
    }, 100);
  }, []);

  const play = useCallback((text: string) => {
    console.log('[TTS] play() called');
    console.log(`[TTS] Input text length: ${text.length}`);
    console.log(`[TTS] Input text preview: "${text.slice(0, 200)}..."`);

    // A. ブラウザ対応チェック
    const hasSpeechSynthesis = typeof window !== 'undefined' && 'speechSynthesis' in window;
    const hasUtterance = typeof window !== 'undefined' && 'SpeechSynthesisUtterance' in window;
    console.log(`[TTS] Browser support: { hasSpeechSynthesis: ${hasSpeechSynthesis}, hasUtterance: ${hasUtterance} }`);

    if (!hasSpeechSynthesis || !hasUtterance) {
      console.error('[TTS] Browser does not support Web Speech API');
      setStatus('error');
      setErrorMessage('このブラウザは音声読み上げに対応していません');
      return;
    }

    // 再生前に必ずキャンセル（残骸除去）
    console.log('[TTS] Cancelling any existing speech');
    cancelSynth();
    isCancelledRef.current = false;

    // C. テキストが空でないかチェック
    if (!text || text.trim().length === 0) {
      console.error('[TTS] Input text is empty');
      setStatus('error');
      setErrorMessage('読み上げるテキストがありません');
      return;
    }

    const chunks = splitChunks(text);
    console.log(`[TTS] Text split into ${chunks.length} chunks`);
    
    if (chunks.length === 0) {
      console.error('[TTS] No chunks created');
      setStatus('error');
      setErrorMessage('テキストの分割に失敗しました');
      return;
    }

    chunksRef.current = chunks;
    indexRef.current = 0;

    setErrorMessage('');
    setStatus('playing');
    
    console.log('[TTS] Starting playback');
    speakChunk(0);
  }, [cancelSynth, speakChunk]);

  const pause = useCallback(() => {
    console.log('[TTS] pause() called');
    if (window.speechSynthesis) {
      window.speechSynthesis.pause();
      console.log(`[TTS] After pause: speaking=${window.speechSynthesis.speaking}, paused=${window.speechSynthesis.paused}`);
      setStatus('paused');
    }
  }, []);

  const resume = useCallback(() => {
    console.log('[TTS] resume() called');
    if (window.speechSynthesis) {
      window.speechSynthesis.resume();
      console.log(`[TTS] After resume: speaking=${window.speechSynthesis.speaking}, paused=${window.speechSynthesis.paused}`);
      setStatus('playing');
    }
  }, []);

  const stop = useCallback(() => {
    console.log('[TTS] stop() called');
    cancelSynth();
    chunksRef.current = [];
    indexRef.current = 0;
    setStatus('idle');
  }, [cancelSynth]);

  // アンマウント時 & ページ離脱時にキャンセル
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('[TTS] beforeunload event');
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log('[TTS] Component unmounting');
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  return { status, rate, errorMessage, play, pause, resume, stop, setRate };
}
